import { Button } from "@vds/buttons";
import { Input } from "@vds/inputs";
import { Loader } from "@vds/loaders";
import { Modal } from "@vds/modals";
import { Notification } from "@vds/notifications";
import { Body, Title } from "@vds/typography";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateCameraCredentials } from "../../../api/DeviceServiceManager";
import { showNotificationBanner, getCameraCredsRequest, resetCameraCreds } from "../../../store/actions";
import { RESET_CREDS_MODAL } from "../../../global/constants/devices";

const ResetCredsModal = ({
  title,
  showModal = false,
  setShowModal,
  selectedRowId,
}) => {
  const data = useSelector((state) => state.deviceReducer);
  const companionList = data.companionDeviceList;
  const cameraCreds = useSelector((state) => state.deviceReducer.cameraCreds);
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );

  const [deviceInfo, setDeviceInfo] = useState({});
  const [cameraUsername, setCameraUsername] = useState("");
  const [cameraPassword, setCameraPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = React.useState({
    show: false,
    type: "",
    message: "",
    subtitle: "",
  });

  const dispatch = useDispatch();

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleReset = (e) => {
    handleFormSubmit(e);
  };

  const handleFormSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    try {
      const credentialsResponse = await updateCameraCredentials(
        deviceInfo,
        cameraUsername,
        cameraPassword,
        activeSession
      );
      if (credentialsResponse.success) {
        const notificationInfo = {
          type: RESET_CREDS_MODAL.UPDATE_SUCCESS_TYPE,
          title: RESET_CREDS_MODAL.UPDATE_SUCCESS_TITLE,
          showNotification: true,
          priority: RESET_CREDS_MODAL.PRIORITY_SUCCESS,
        };
        dispatch(showNotificationBanner(notificationInfo));
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const notificationInfo = {
        type: RESET_CREDS_MODAL.UPDATE_SUCCESS_TYPE,
        title: RESET_CREDS_MODAL.UPDATE_ERROR_TITLE,
        showNotification: true,
        priority: RESET_CREDS_MODAL.PRIORITY_CRITICAL,
      };
      dispatch(showNotificationBanner(notificationInfo));
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  const handleRetryFetchCreds = () => {
    if (deviceInfo[0]?.id) {
      dispatch(getCameraCredsRequest({ qrcode: deviceInfo[0].id }));
    }
  };

  const renderStepContent = () => {
    return (
      <>
        <Title bold={true} size="medium">
          {RESET_CREDS_MODAL.TITLE}
        </Title>
        <div style={{ marginTop: "5%", marginBottom: "5%" }}>
          <Body size="medium">
            {RESET_CREDS_MODAL.DESCRIPTION}
          </Body>
        </div>

        {cameraCreds.error && (
          <div style={{ marginBottom: "4%" }}>
            <Notification
              type="critical"
              title={RESET_CREDS_MODAL.FETCH_ERROR_TITLE}
              subtitle={cameraCreds.error}
              fullBleed={false}
              hideCloseButton={true}
              buttonData={[
                {
                  children: RESET_CREDS_MODAL.REFETCH_BUTTON,
                  onClick: handleRetryFetchCreds,
                  "data-testid": RESET_CREDS_MODAL.TEST_ID_REFETCH_BUTTON
                }
              ]}
            />
          </div>
        )}

        <div
          style={{
            marginTop: "4%",
            marginBottom: "4%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Input
            type="text"
            label={RESET_CREDS_MODAL.USERNAME_LABEL}
            required={true}
            readOnly={false}
            width="48%"
            value={cameraUsername}
            disabled={cameraCreds.loading}
            onChange={(e) => setCameraUsername(e.target.value)}
          />
          <Input
            type="password"
            label={RESET_CREDS_MODAL.PASSWORD_LABEL}
            mask="toggle"
            width="48%"
            readOnly={false}
            required={true}
            value={cameraPassword}
            disabled={cameraCreds.loading}
            onChange={(e) => setCameraPassword(e.target.value)}
          />
        </div>
        <div
          style={{
            marginBottom: "5%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            size="medium"
            width="45%"
            disabled={cameraCreds.loading || !cameraUsername || !cameraPassword}
            use="primary"
            data-testid={RESET_CREDS_MODAL.TEST_ID_RESET_BUTTON}
            onClick={handleReset}
          >
            {RESET_CREDS_MODAL.RESET_BUTTON}
          </Button>
          <Button
            size="medium"
            width="45%"
            disabled={false}
            use="secondary"
            data-testid={RESET_CREDS_MODAL.TEST_ID_CANCEL_BUTTON}
            onClick={() => {
              handleCloseModal();
            }}
          >
            {RESET_CREDS_MODAL.CANCEL_BUTTON}
          </Button>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (companionList?.length > 0) {
      const companionObj = companionList?.filter((item) => {
        return item.refid === selectedRowId;
      });
      setDeviceInfo(companionObj);
    }
  }, [selectedRowId]);

  useEffect(() => {
    if (showModal && deviceInfo[0]?.id) {
      dispatch(resetCameraCreds());
      dispatch(getCameraCredsRequest({ qrcode: deviceInfo[0].id }));
    }
  }, [showModal, deviceInfo]);

  useEffect(() => {
    if (cameraCreds.data && (cameraCreds.data.Username || cameraCreds.data.Password)) {
      setCameraUsername(cameraCreds.data.Username || "");
      setCameraPassword(cameraCreds.data.Password || "");
    }
  }, [cameraCreds.data]);

  return (
    <div>
      {notification.show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1050,
            display: "flex",
            justifyContent: "center",
            width: "auto",
          }}
        >
          <Notification
            type={notification.type}
            title={notification.message}
            fullBleed={false}
            inline={false}
            disableFocus={true}
          />
        </div>
      )}
      <Modal
        opened={showModal}
        onOpenedChange={(opened) => {
          if (!opened) {
            handleCloseModal();
          }
        }}
        ariaLabel={title}
      >
        {renderStepContent()}
        {(loading || cameraCreds.loading) && <Loader active fullscreen={false} surface="light" />}
      </Modal>
    </div>
  );
};

export default ResetCredsModal;
